import React, { useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { userTimezoneHeaders } from '../../utils/userTimezoneHeader';

declare const process: { env: Record<string, string | undefined> };

/** Mark Zones canvas bitmap size — must match `ZONE_MARK_CANVAS_*` in `src/utils/zoneStreamCoordinates.ts`. */
const ZONE_MARK_CANVAS_WIDTH = 600;
const ZONE_MARK_CANVAS_HEIGHT = 400;
const ZONES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

/**
 * Parse `linked_camera_ai_config_count` from GET /zones/{zone_id}
 * (e.g. `{ data: { id, name, linked_camera_ai_config_count: 1, ... } }`).
 */
function parseLinkedCameraAiConfigCountFromZonesGetBody(body: unknown): number {
  if (body == null || typeof body !== 'object') return 0;
  const root = body as Record<string, unknown>;
  const read = (obj: Record<string, unknown>): number | null => {
    const raw =
      obj.linked_camera_ai_config_count ?? obj.linkedCameraAiConfigCount;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim() !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };
  const data = root.data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const n = read(data as Record<string, unknown>);
    if (n !== null) return n;
  }
  return read(root) ?? 0;
}

/**
 * Preflight for delete dialog: GET /zones/{zone_id} → use `data.linked_camera_ai_config_count`.
 */
async function fetchLinkedCameraAiConfigCountForDeletePreflight(
  zoneId: string,
): Promise<number> {
  const base = ZONES_API_BASE.replace(/\/$/, '');
  if (!base) return 0;
  const res = await fetch(`${base}/zones/${encodeURIComponent(zoneId)}`, {
    method: 'GET',
    headers: { Accept: 'application/json', ...userTimezoneHeaders() },
  });
  if (!res.ok) return 0;
  const body: unknown = await res.json().catch(() => null);
  return parseLinkedCameraAiConfigCountFromZonesGetBody(body);
}

export interface DrawnZone {
  id: string;
  name: string;
  type: 'polygon' | 'line';
  points: { x: number; y: number }[];
  color: string;
  isActive: boolean;
  createdAt: Date;
}

/** Full-frame zone (by display name): hide Delete in the row ⋮ menu. */
function isFullFrameZone(zone: Pick<DrawnZone, 'name'>): boolean {
  const n = zone.name.trim().replace(/\s+/g, ' ').toLowerCase();
  return n === 'full frame';
}

/**
 * Visuals aligned with `ZoneDrawingCanvas` `drawZone` (Mark Zones): stroke + fill for polygon,
 * stroke + vertex dots for line.
 */
const ZONE_MARK_STROKE = '#FF8555';
const ZONE_MARK_POLYGON_FILL = 'rgba(179, 144, 115, 0.4)';
const ZONE_MARK_STROKE_PX = 2;
/** Matches `ZoneDrawingCanvas` vertex handle radius (canvas px). */
const ZONE_MARK_POINT_RADIUS = 6;
const MIN_POINTS_POLYGON_PREVIEW = 3;
/** Fits default zone row height (40px) with cell padding. */
const ZONE_TYPE_PREVIEW_BOX_PX = 36;

/** `points` are in Mark Zones canvas space (600×400), same as {@link ZoneDrawingCanvas}. */
function canvasPointsToSvgPointsAttr(
  points: Array<{ x: number; y: number }>,
): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

/**
 * Zoomed viewBox around the drawn shape so the small thumbnail shows the same geometry
 * as Mark Zones (not a tiny speck in the full 600×400 frame).
 */
function getMarkZoneShapePreviewViewBox(
  points: Array<{ x: number; y: number }>,
): string {
  const W = ZONE_MARK_CANVAS_WIDTH;
  const H = ZONE_MARK_CANVAS_HEIGHT;
  const pad = ZONE_MARK_POINT_RADIUS + ZONE_MARK_STROKE_PX + 6;
  const minSide = 40;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
    return `0 0 ${W} ${H}`;
  }

  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;

  let bw = maxX - minX;
  let bh = maxY - minY;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  if (bw < minSide) {
    bw = minSide;
    minX = cx - bw / 2;
  }
  if (bh < minSide) {
    bh = minSide;
    minY = cy - bh / 2;
  }

  return `${minX} ${minY} ${bw} ${bh}`;
}

/**
 * Renders the real zone geometry (same canvas coordinates as Mark Zones), scaled to fit
 * the thumbnail via a tight viewBox — line vs polygon matches `zone.type` / point count.
 */
function ZoneTypeCanvasShapePreview({
  zone,
}: {
  zone: Pick<DrawnZone, 'type' | 'points'>;
}) {
  const pts = zone.points ?? [];
  const isLine = zone.type === 'line' || pts.length === 2;
  const pointsAttr = canvasPointsToSvgPointsAttr(pts);
  const viewBox =
    pts.length > 0 ? getMarkZoneShapePreviewViewBox(pts) : `0 0 ${ZONE_MARK_CANVAS_WIDTH} ${ZONE_MARK_CANVAS_HEIGHT}`;

  const svgCommon = {
    width: '100%' as const,
    height: '100%' as const,
    viewBox,
    preserveAspectRatio: 'xMidYMid meet' as const,
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true as const,
  };

  if (pts.length === 0) {
    return <ZoneTypeNoCoordinatesPlaceholder />;
  }

  if (isLine && pts.length >= 2) {
    return (
      <svg {...svgCommon}>
        <polyline
          points={pointsAttr}
          fill="none"
          stroke={ZONE_MARK_STROKE}
          strokeWidth={ZONE_MARK_STROKE_PX}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {pts.map((p, i) => (
          <circle
            key={`v-${i}`}
            cx={p.x}
            cy={p.y}
            r={ZONE_MARK_POINT_RADIUS}
            fill={ZONE_MARK_STROKE}
            stroke={ZONE_MARK_STROKE}
            strokeWidth={ZONE_MARK_STROKE_PX}
          />
        ))}
      </svg>
    );
  }

  if (!isLine && pts.length >= MIN_POINTS_POLYGON_PREVIEW) {
    return (
      <svg {...svgCommon}>
        <polygon
          points={pointsAttr}
          fill={ZONE_MARK_POLYGON_FILL}
          stroke={ZONE_MARK_STROKE}
          strokeWidth={ZONE_MARK_STROKE_PX}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {pts.map((p, i) => (
          <circle
            key={`v-${i}`}
            cx={p.x}
            cy={p.y}
            r={ZONE_MARK_POINT_RADIUS}
            fill={ZONE_MARK_STROKE}
            stroke={ZONE_MARK_STROKE}
            strokeWidth={ZONE_MARK_STROKE_PX}
          />
        ))}
      </svg>
    );
  }

  return <ZoneTypeNoCoordinatesPlaceholder />;
}

/** Shown when coordinates are missing or incomplete — never a fake triangle/square icon. */
function ZoneTypeNoCoordinatesPlaceholder() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="3.5"
        y="3.5"
        width="17"
        height="17"
        rx="2"
        fill="none"
        stroke="#6B7280"
        strokeWidth="1"
        strokeDasharray="3 2"
        opacity={0.85}
      />
    </svg>
  );
}

function getZoneVertexCount(zone: Pick<DrawnZone, 'type' | 'points'>): number {
  return zone.points?.length ?? 0;
}

/** Same categories as Mark Zones: line vs closed polygon (shape itself is in the preview). */
function getZoneShapeLabel(zone: Pick<DrawnZone, 'type' | 'points'>): string {
  const n = getZoneVertexCount(zone);
  if (zone.type === 'line' || n === 2) return 'Line';
  return 'Polygon';
}

/**
 * Black rounded tile (Mark Zones canvas look); inner SVG uses real 600×400 canvas coordinates.
 */
function ZoneTypeMarkPreview({ zone }: { zone: Pick<DrawnZone, 'type' | 'points'> }) {
  const box = ZONE_TYPE_PREVIEW_BOX_PX;
  return (
    <Box
      sx={{
        width: box,
        height: box,
        flexShrink: 0,
        borderRadius: '6px',
        // bgcolor: '#000000',
        // border: '1px solid #2D2D2D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
      aria-hidden
    >
      <ZoneTypeCanvasShapePreview zone={zone} />
    </Box>
  );
}

/** Icon: square outline with four small squares at corners (empty zones state). */
function ZonesEmptyIcon({ sx }: { sx?: object }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden
      sx={{ width: 60, height: 60, color: '#9CA3AF', ...sx }}
    >
      <path fill="none" stroke="currentColor" strokeWidth={1.2} d="M5 5h14v14H5z" />
      <path
        fill="currentColor"
        d="M5 5m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM19 5m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM19 19m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM5 19m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0z"
      />
    </Box>
  );
}

interface ZonesTabProps {
  onDrawingStateChange?: (isDrawing: boolean) => void;
  zones?: DrawnZone[];
  onDeleteZone?: (zoneId: string) => void;
  onEditZone?: (zone: DrawnZone) => void;
  /** When set, shown in the center of the table (e.g. fetch error). */
  zonesError?: string | null;
  /**
   * When false, Add Zone is disabled (e.g. Zone Configuration has no preview / playable stream on Mark Zones).
   * @default true
   */
  markZonesSurfaceReady?: boolean;
  /**
   * When false, Add Zone is disabled (camera reached max zones per configuration).
   * @default true
   */
  canAddMoreZones?: boolean;
  /** Shown in tooltip when Add Zone is disabled due to limit. @default 30 */
  maxZonesPerCamera?: number;
  /** Row highlight (peach) when this zone is selected (e.g. open for edit). Hover uses a neutral tint for other rows. */
  selectedZoneId?: string | null;
  /**
   * When the user clicks a zone row (not the ⋮ menu), parent can sync Mark Zones highlight.
   */
  onZoneRowSelect?: (zone: DrawnZone) => void;
  /**
   * When true (e.g. Zone Configuration opened via "View Details"), hide add/edit/delete for zones.
   * @default false
   */
  readOnly?: boolean;
}

type Order = 'asc' | 'desc';
type ZoneOrderBy = keyof Pick<DrawnZone, 'name' | 'type'>;

export function ZonesTab({
  onDrawingStateChange,
  zones: externalZones = [],
  onDeleteZone,
  onEditZone,
  zonesError = null,
  markZonesSurfaceReady = true,
  canAddMoreZones = true,
  maxZonesPerCamera = 30,
  selectedZoneId = null,
  onZoneRowSelect,
  readOnly = false,
}: ZonesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<{
    zone: DrawnZone;
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<DrawnZone | null>(null);
  const [deletePreflightLoading, setDeletePreflightLoading] = useState(false);
  /** True when GET /zones/{id} has `data.linked_camera_ai_config_count` > 0. */
  const [deleteMultiLinkWarning, setDeleteMultiLinkWarning] = useState(false);
  const [hoveredZoneId, setHoveredZoneId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<ZoneOrderBy>('name');
  /** Name-column sort arrows only after user clicks Name (not on first paint). */
  const [nameSortEngaged, setNameSortEngaged] = useState(false);

  // Use external zones from parent
  const zones = externalZones;

  const addZoneAllowed =
    !readOnly && markZonesSurfaceReady && canAddMoreZones;

  // Handle Add Zone button
  const handleAddZone = () => {
    if (!addZoneAllowed) return;
    if (onDrawingStateChange) {
      onDrawingStateChange(true);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, zone: DrawnZone) => {
    event.stopPropagation();
    setAnchorEl({
      zone,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleContextMenu = (event: React.MouseEvent, zone: DrawnZone) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl({
      zone,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle Edit
  const handleEdit = () => {
    if (anchorEl?.zone && onEditZone) {
      onEditZone(anchorEl.zone);
    }
    handleMenuClose();
  };

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setZoneToDelete(null);
    setDeletePreflightLoading(false);
    setDeleteMultiLinkWarning(false);
  }, []);

  // Handle Delete — GET /zones/{zone_id} → linked_camera_ai_config_count > 0 → linked message, else normal
  const handleDelete = () => {
    if (anchorEl?.zone && isFullFrameZone(anchorEl.zone)) {
      handleMenuClose();
      return;
    }
    if (anchorEl?.zone) {
      const zone = anchorEl.zone;
      setZoneToDelete(zone);
      setDeleteDialogOpen(true);
      setDeletePreflightLoading(true);
      setDeleteMultiLinkWarning(false);
      void fetchLinkedCameraAiConfigCountForDeletePreflight(zone.id)
        .then((linkedCameraAiConfigCount) => {
          setDeleteMultiLinkWarning(linkedCameraAiConfigCount > 0);
        })
        .catch(() => {
          setDeleteMultiLinkWarning(false);
        })
        .finally(() => {
          setDeletePreflightLoading(false);
        });
    }
    handleMenuClose();
  };

  const confirmDelete = () => {
    if (zoneToDelete && onDeleteZone) {
      onDeleteZone(zoneToDelete.id);
      console.log('Zone moved to recycle bin:', zoneToDelete);
    }
    closeDeleteDialog();
  };

  const handleSort = (property: ZoneOrderBy) => {
    if (property === 'name') setNameSortEngaged(true);
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /** Search then sort (same toggle pattern as ManageUseCaseTable). */
  const filteredZones = useMemo(() => {
    const query = searchQuery.toLowerCase();
    let list = zones.filter((zone) => zone.name.toLowerCase().includes(query));

    list.sort((a, b) => {
      if (orderBy === 'type') {
        const aVal = a.type;
        const bVal = b.type;
        if (aVal == null || bVal == null) return 0;
        if (aVal !== bVal) {
          const cmp = aVal.localeCompare(bVal);
          return order === 'asc' ? cmp : -cmp;
        }
        const na = getZoneVertexCount(a);
        const nb = getZoneVertexCount(b);
        if (na !== nb) return order === 'asc' ? na - nb : nb - na;
        return 0;
      }
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null || bValue == null) return 0;
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [zones, searchQuery, order, orderBy]);

  // Paginate
  const paginatedZones = filteredZones.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const totalItems = filteredZones.length;
  const zoneLabel = zones.length === 1 ? 'zone' : 'zones';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
      }}
    >
      {/* Section title: Zone X of Y zone(s) */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: 14,
            color: '#4A5565',
          }}
        >
          Zone <span style={{ color: '#2932E5', fontWeight: 600 }}>{totalItems === 0 ? 0 : Math.min(page * rowsPerPage + 1, totalItems)}</span> of {zones.length} {zoneLabel}
        </Typography>
      </Box>

      {/* Action bar: Search (left), Add Zone (right) */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <TextField
          size="small"
          placeholder="Search zone..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 220,
            '& .MuiOutlinedInput-root': {
              height: 40,
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              bgcolor: '#fff',
              borderRadius: '8px',
            },
          }}
        />

        <Tooltip
          title={
            readOnly
              ? 'View only: open this camera with Edit Zone to add or change zones.'
              : !markZonesSurfaceReady
                ? 'Cannot create zone: no preview in Mark Zones, or the stream is unavailable / unsupported in the browser.'
                : !canAddMoreZones
                  ? `This camera already has the maximum of ${maxZonesPerCamera} zones.`
                  : 'Draw a new zone on the Mark Zones preview'
          }
          arrow
          enterDelay={300}
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddZone}
              disabled={!addZoneAllowed}
              sx={{
                textTransform: 'none',
                fontFamily: 'Roboto, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                bgcolor: '#2932E5',
                borderRadius: '8px',
                px: 2,
                py: 1,
                '&:hover': {
                  bgcolor: '#1e28b5',
                },
                '&.Mui-disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              Add Zone
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#fff',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Name (sortable — same behavior as ManageUseCaseTable) */}
              <TableCell
                onClick={() => handleSort('name')}
                sx={{
                  width: '50%',
                  minWidth: 200,
                  textAlign: 'left',
                  px: 2,
                  py: 1.25,
                  bgcolor: '#fff',
                  borderBottom: '1px solid #E5E7EB',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 500,
                      fontSize: 14,
                      color: '#1976D2',
                      letterSpacing: '0.1px',
                    }}
                  >
                    Name
                  </Typography>
                  {nameSortEngaged &&
                    orderBy === 'name' &&
                    (order === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" sx={{ color: '#1976D2', fontSize: 18 }} />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" sx={{ color: '#1976D2', fontSize: 18 }} />
                    ))}
                </Box>
              </TableCell>

              {/* Zone Type (sortable) */}
              <TableCell
                onClick={() => handleSort('type')}
                sx={{
                  width: '40%',
                  minWidth: 150,
                  textAlign: 'left',
                  px: 2,
                  py: 1.25,
                  bgcolor: '#fff',
                  borderBottom: '1px solid #E5E7EB',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 500,
                      fontSize: 14,
                      color: '#1976D2',
                      letterSpacing: '0.1px',
                    }}
                  >
                    Zone Type
                  </Typography>
                  {orderBy === 'type' &&
                    (order === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" sx={{ color: '#1976D2', fontSize: 18 }} />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" sx={{ color: '#1976D2', fontSize: 18 }} />
                    ))}
                </Box>
              </TableCell>

              {!readOnly && (
                <TableCell
                  sx={{
                    width: 80,
                    minWidth: 80,
                    textAlign: 'center',
                    px: 2,
                    py: 1.25,
                    bgcolor: '#fff',
                    borderBottom: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 500,
                      fontSize: 14,
                      color: '#1976D2',
                      letterSpacing: '0.1px',
                      opacity:
                        hoveredZoneId != null || anchorEl != null ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                    }}
                  >
                    Actions
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedZones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 2 : 3} align="center" sx={{ py: 24, borderBottom: 'none', verticalAlign: 'middle' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <ZonesEmptyIcon />
                    <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: zonesError ? '#D32F2F' : '#4A5565' }}>
                      {zonesError ?? 'No zone available'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedZones.map((zone) => {
                const isSelected =
                  selectedZoneId != null && zone.id === selectedZoneId;
                return (
                <TableRow
                  key={zone.id}
                  onClick={() => onZoneRowSelect?.(zone)}
                  onMouseEnter={() => setHoveredZoneId(zone.id)}
                  onMouseLeave={() => setHoveredZoneId(null)}
                  onContextMenu={(e) => {
                    if (!readOnly) handleContextMenu(e, zone);
                  }}
                  sx={{
                    height: 40,
                    cursor: onZoneRowSelect ? 'pointer' : 'default',
                    bgcolor: isSelected ? '#FFE0D0' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected
                        ? '#FFE0D0'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {/* Name */}
                  <TableCell
                    sx={{
                      borderBottom: '1px solid #E5E7EB',
                      px: 1,
                      py: 0.5,
                      height: 40,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        fontFamily: 'Roboto, sans-serif',
                      }}
                    >
                      {zone.name}
                    </Typography>
                  </TableCell>

                  {/* Zone Type */}
                  <TableCell
                    sx={{
                      borderBottom: '1px solid #E5E7EB',
                      px: 1,
                      py: 0.5,
                      height: 40,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <ZoneTypeMarkPreview zone={zone} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '13px',
                          color: 'text.secondary',
                        }}
                      >
                        {getZoneShapeLabel(zone)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {!readOnly && (
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid #E5E7EB',
                        px: 1,
                        py: 0.5,
                        height: 40,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, zone);
                        }}
                        sx={{
                          opacity: hoveredZoneId === zone.id ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                          pointerEvents:
                            hoveredZoneId === zone.id ? 'auto' : 'none',
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer: Total Items (left), Rows per page + range + arrows (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid #E5E7EB',
          bgcolor: '#fff',
          minHeight: 52,
          px: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: 14,
            color: '#4A5565',
          }}
        >
          Total Items: {totalItems}
        </Typography>
        <TablePagination
          component="div"
          count={filteredZones.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          sx={{
            '& .MuiTablePagination-toolbar': {
              minHeight: 52,
              px: 0,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 14,
              fontFamily: 'Roboto, sans-serif',
              color: '#4A5565',
              my: 0,
            },
          }}
        />
      </Box>

      {/* Actions menu: 3-dot or right-click on row */}
      <Menu
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorEl
            ? { top: anchorEl.mouseY + 2, left: anchorEl.mouseX + 2 }
            : undefined
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1, color: '#4A5565' }} />
          Edit
        </MenuItem>
        {anchorEl &&
        onDeleteZone &&
        !isFullFrameZone(anchorEl.zone) ? (
          <MenuItem onClick={handleDelete} sx={{ color: '#D32F2F' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        ) : null}
      </Menu>

      {/* Delete Confirmation Dialog — preflight GET /zones/{zone_id} (`data.linked_camera_ai_config_count`) */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 500,
          }}
        >
          Delete Zone?
        </DialogTitle>
        <DialogContent>
          {deletePreflightLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={32} sx={{ color: '#2932E5' }} />
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: '#4A5565',
              }}
            >
              {deleteMultiLinkWarning ? (
                <>
                  This zone has linked camera AI or zone profile configuration. Do you want to delete
                  this item? This zone will be moved to the recycle bin and will stay for 60 days. You
                  can restore or permanently delete it from the bin.
                </>
              ) : (
                <>
                  This zone will be moved to the recycle bin and will stay for 60 days. You can
                  restore or permanently delete it from the bin.
                </>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteDialog}
            disabled={deletePreflightLoading}
            sx={{
              color: '#4A5565',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            disabled={deletePreflightLoading}
            sx={{
              bgcolor: '#D32F2F',
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#B71C1C',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}